"use client";

import { useState, useCallback } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { mediaContent } from "@/config/content";
import { useTranslation } from "@/i18n";
import { SectionWrapper, SectionEyebrow, SectionHeadline } from "@/components/ui/SectionWrapper";
import { FadeIn } from "@/components/ui/AnimatedSection";

const TOTAL = mediaContent.items.length;

const slideVariants = {
    enter: (dir: number) => ({ x: dir > 0 ? "100%" : "-100%" }),
    center: { x: 0 },
    exit: (dir: number) => ({ x: dir > 0 ? "-100%" : "100%" }),
};

export function Media() {
    const [current, setCurrent] = useState(0);
    const [direction, setDirection] = useState(1);
    const [lightbox, setLightbox] = useState(false);
    const { t } = useTranslation();

    const go = useCallback((index: number, dir: number) => {
        setDirection(dir);
        setCurrent(index);
    }, []);

    const prev = useCallback(() => go((current - 1 + TOTAL) % TOTAL, -1), [current, go]);
    const next = useCallback(() => go((current + 1) % TOTAL, 1), [current, go]);

    const item = mediaContent.items[current];
    const isFirst = current === 0;

    return (
        <SectionWrapper id="media" className="py-(--section-y-lg)">
            <div className="max-w-3xl mb-12">
                <FadeIn>
                    <SectionEyebrow>{t.media.eyebrow}</SectionEyebrow>
                    <SectionHeadline>{t.media.headline}</SectionHeadline>
                    <p className="text-lg text-text-secondary leading-relaxed">
                        {t.media.description}
                    </p>
                </FadeIn>
            </div>

            {/* Screenshot carousel */}
            <FadeIn>
                <div
                    className="relative"
                    role="region"
                    aria-label="Screenshot carousel"
                    aria-roledescription="carousel"
                >
                    {/* Slide track */}
                    <div className="relative aspect-video w-full rounded-xl overflow-hidden bg-surface border border-border">
                        <AnimatePresence initial={false} custom={direction}>
                            <motion.button
                                key={current}
                                custom={direction}
                                variants={slideVariants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                className="absolute inset-0 w-full h-full cursor-zoom-in"
                                onClick={() => setLightbox(true)}
                                aria-label={`View screenshot ${current + 1} of ${TOTAL} fullscreen`}
                                aria-roledescription="slide"
                            >
                                <Image
                                    src={item.src}
                                    alt={item.alt}
                                    fill
                                    className="object-cover"
                                    sizes="(max-width: 768px) 100vw, (max-width: 1280px) 90vw, 1280px"
                                    priority={isFirst}
                                    loading={isFirst ? "eager" : "lazy"}
                                />
                            </motion.button>
                        </AnimatePresence>

                        {/* Prev arrow */}
                        <button
                            onClick={prev}
                            className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-void/70 border border-border flex items-center justify-center text-text hover:bg-mars/20 hover:border-mars/50 transition-colors"
                            aria-label="Previous screenshot"
                        >
                            <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5" aria-hidden="true">
                                <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>

                        {/* Next arrow */}
                        <button
                            onClick={next}
                            className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-void/70 border border-border flex items-center justify-center text-text hover:bg-mars/20 hover:border-mars/50 transition-colors"
                            aria-label="Next screenshot"
                        >
                            <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5" aria-hidden="true">
                                <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>

                        {/* Counter */}
                        <div
                            className="absolute top-3 right-3 z-10 px-2.5 py-1 rounded-full bg-void/70 border border-border text-xs text-text-secondary"
                            aria-live="polite"
                            aria-atomic="true"
                        >
                            {current + 1} / {TOTAL}
                        </div>
                    </div>

                    {/* Dot indicators */}
                    <div
                        className="flex items-center justify-center gap-2 mt-4"
                        role="tablist"
                        aria-label="Screenshot navigation"
                    >
                        {mediaContent.items.map((_, i) => (
                            <button
                                key={i}
                                role="tab"
                                aria-selected={i === current}
                                aria-label={`Go to screenshot ${i + 1}`}
                                onClick={() => go(i, i > current ? 1 : -1)}
                                className={`h-1.5 rounded-full transition-all duration-300 ${i === current ? "w-6 bg-mars" : "w-1.5 bg-text-muted hover:bg-text-secondary"
                                    }`}
                            />
                        ))}
                    </div>
                </div>
            </FadeIn>

            {/* Lightbox */}
            <AnimatePresence>
                {lightbox && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[100] bg-void/90 backdrop-blur-sm flex items-center justify-center p-8"
                        onClick={() => setLightbox(false)}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Screenshot viewer"
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                            className="relative max-w-4xl w-full aspect-video rounded-xl overflow-hidden bg-surface border border-border"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Image
                                src={item.src}
                                alt={item.alt}
                                fill
                                className="object-contain"
                                sizes="(max-width: 1280px) 100vw, 1280px"
                                priority
                            />

                            {/* Close button */}
                            <button
                                onClick={() => setLightbox(false)}
                                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-void/80 border border-border flex items-center justify-center text-text-secondary hover:text-text transition-colors cursor-pointer z-10"
                                aria-label="Close viewer"
                            >
                                <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4" aria-hidden="true">
                                    <path
                                        d="M18 6L6 18M6 6l12 12"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </SectionWrapper>
    );
}
